import { SnackBarService } from '@shared/ui/snack-bar/snack-bar.service';
import { ProgressBarOverlayService } from '@shared/ui/progress-bar-overlay/progress-bar-overlay.service';
import { Injectable, PLATFORM_ID, effect, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UserActions } from '../actions/user.actions';
import { Base64 } from '@shared/utils/classes/base64';
import dayjs from 'dayjs';
import { SsrCookieService } from '@shared/utils/services/ssr-cookie-service.service';
import {
  IPLocation,
  UpdateUserProfilePayload,
  User,
  Notification,
  UserProfile,
  UserType,
  UserAnnouncement,
  UserSubscriptionStatus
} from '@core/utils/models/user.model';
import { selectLogoutHttpState, selectUser, selectUserProfile } from '../selectors/user.selectors';
import { HttpResponse, HttpStatus } from '@shared/utils/models/http.model';
import { HttpHandler } from '@shared/utils/classes/http-handler';
import { Observable, filter, first } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  getNextRequiredPendingStep,
  onboardingRequiredStepsCompleted,
} from '@features/onboarding/utils/guards/onboarding.guard';
import { Router } from '@angular/router';
import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { AnnouncementDialogComponent } from '@core/ui/announcement-dialog/announcement-dialog.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class UserService extends HttpHandler {
  private readonly store = inject(Store);
  private readonly cookieService = inject(SsrCookieService);
  private readonly actions$ = inject(Actions);
  private readonly progressBarOverlayService = inject(ProgressBarOverlayService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly dialog = inject(MatDialog);
  private readonly location = inject(Location);

  private readonly userCookieName = `9a4e5c4f-1f6d-4b6c-9c4d-8a6e5b4c4f`;
  private readonly udidCookieName = `UDID`;

  private readonly user = this.store.selectSignal(selectUser);
  private readonly profile = this.store.selectSignal(selectUserProfile);
  private readonly logoutHttpState = this.store.selectSignal(selectLogoutHttpState);

  constructor() {
    super();
    effect(() => {
      if (this.logoutHttpState().status === HttpStatus.loading) {
        this.progressBarOverlayService.show();
        return;
      }

      this.progressBarOverlayService.hide();
      if (this.logoutHttpState().status === HttpStatus.error) {
        this.snackBarService.open(`حدث خطأ أثناء محاولة الخروج. يرجى المحاولة مجددا`, 'error');
      }
    });
  }

  init(): void {
    this.listen();

    if (this.user()) {
      if (this.profile() || this.user()?.user_type !== UserType.user) {
        return;
      }

      this.store.dispatch(UserActions.getProfile());
      this.store.dispatch(UserActions.getNotifications());

      return;
    }

    const user: User | null = this.getUserFromCookie();
    user && this.store.dispatch(UserActions.setUser({ user, source: 'cookie' }));
  }

  listen(): void {
    this.actions$.pipe(ofType(UserActions.setUser, UserActions.clearUser, UserActions.getProfileSuccess)).subscribe({
      next: (action) => {
        switch (action.type) {
          case UserActions.setUser.type:
            if (action.source === 'api') {
              this.saveUserToCookie(action.user);
            }
            if (action.user.user_type === UserType.user) {
              this.store.dispatch(UserActions.getProfile());
              this.store.dispatch(UserActions.getNotifications());
            }
            break;
          case UserActions.clearUser.type:
            this.deleteUserFromCookie();
            break;
          case UserActions.getProfileSuccess.type:
            this.checkOnboardingStatus();
            break;
        }
      },
    });

    if (isPlatformServer(this.platformId)) return;

    toObservable(this.profile)
      .pipe(filter(Boolean), first())
      .subscribe({
        next: (profile) => {
          const url = this.location.path();

          if (url.startsWith('/payment')) {
            return;
          }

          if (
            profile.announcement &&
            profile.subscription.subscription_status === UserSubscriptionStatus.NOT_SUBSCRIBED
          ) {
            this.openAnnouncementDialog(profile.announcement);
          }
        },
      });
  }

  getUserFromCookie(): User | null {
    const userB64: string | undefined = this.cookieService.get(this.userCookieName);
    return userB64 ? JSON.parse(Base64.decodeUnicode(userB64)) : null;
  }

  saveUserToCookie(user: User): void {
    const userB64 = Base64.encodeUnicode(JSON.stringify(user));
    this.cookieService.set(this.userCookieName, userB64, dayjs().add(1, 'year').toDate(), '/');
  }

  deleteUserFromCookie(): void {
    this.cookieService.delete(this.userCookieName, '/');
    if (isPlatformBrowser(this.platformId)) {
      location.reload();
    }
  }

  getUniqueDeviceID(): string {
    let udid: string | undefined = this.cookieService.get(this.udidCookieName);

    if (!udid) {
      udid = uuidv4();
      this.cookieService.set(this.udidCookieName, udid, dayjs().add(100, 'year').toDate(), '/');
    }

    return udid;
  }

  checkOnboardingStatus(): void {
    this.store
      .select(selectUserProfile)
      .pipe(filter(Boolean), first())
      .subscribe({
        next: ({ onboarding_status }) => {
          if (!onboardingRequiredStepsCompleted(onboarding_status)) {
            const step = getNextRequiredPendingStep(onboarding_status);
            this.router.navigate(['/onboarding', step?.description]);
          }
        },
      });
  }

  openAnnouncementDialog(announcement: UserAnnouncement): void {
    this.dialog.open<AnnouncementDialogComponent, UserAnnouncement>(AnnouncementDialogComponent, {
      data: announcement,
      width: '600px',
      direction: 'rtl',
      closeOnNavigation: false,
    });
  }

  getUserProfile(): Observable<HttpResponse<[UserProfile]>> {
    const url: string = environment.apiUrls.user.profile.get;
    return this.get<HttpResponse<[UserProfile]>>(url);
  }

  updateUserProfile(payload: Partial<UpdateUserProfilePayload>): Observable<HttpResponse<string>> {
    const url: string = environment.apiUrls.user.profile.update;
    return this.post<HttpResponse<string>>(url, payload);
  }

  uploadAvatar(file: File): Observable<HttpResponse<string>> {
    const url: string = environment.apiUrls.user.profile.uploadAvatar;
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.post<HttpResponse<string>>(url, formData);
  }

  getUserIPLocation(): Observable<IPLocation> {
    const url = `https://api.ipgeolocation.io/ipgeo`;
    return this.get<IPLocation>(url, { params: { apiKey: environment.ipGeolocationAccessToken }, external: true });
  }

  getUserNotifications(): Observable<HttpResponse<{ total: number; messages: Notification[] }>> {
    const url: string = environment.apiUrls.user.notifications;
    return this.get<HttpResponse<{ total: number; messages: Notification[] }>>(url);
  }

  markAnnouncementAsSeen(announcementId: number): Observable<HttpResponse<string>> {
    const url: string = environment.apiUrls.user.markAnnouncementAsSeen;
    return this.post<HttpResponse<string>>(url, { id: announcementId });
  }
}
